import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';

// Keep this flexible - could be relative in dev or absolute in prod
const API_BASE = window.location.hostname === 'localhost' 
  ? '/api' 
  : 'https://storetoyou.ca/api';

const AddProductPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Store information
  const [store, setStore] = useState(null);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  
  // Basic fields for the product
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch user's store information
  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await fetch(`${API_BASE}/user/store`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const storeData = await response.json();
          setStore(storeData);
        } else if (response.status === 401 || response.status === 403) {
          // Token expired or invalid
          localStorage.removeItem('token');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching store info:', error);
      }
    };

    fetchStoreInfo();
  }, [navigate]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE}/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Handle image selection with preview
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Convert price to float
      const floatPrice = parseFloat(price);
      if (isNaN(floatPrice)) {
        setError('Please enter a valid price');
        setLoading(false);
        return;
      }

      // STEP 1: Create the product without the image
      const productData = {
        name,
        description,
        price: floatPrice,
        category_id: categoryId || null
      };

      console.log('Creating product with data:', productData);

      // Use the simpler /api/products endpoint that worked
      const productResponse = await fetch(`${API_BASE}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productData)
      });

      if (!productResponse.ok) {
        const responseText = await productResponse.text();
        let errorMessage = 'Failed to create product';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const newProduct = await productResponse.json();
      console.log('Product created successfully:', newProduct);

      // STEP 2: If image was provided, upload it as a second step
      if (imageFile && newProduct.id) {
        const formData = new FormData();
        formData.append('image', imageFile);
        
        console.log('Uploading image for product ID:', newProduct.id);
        
        const imageResponse = await fetch(`${API_BASE}/products/${newProduct.id}/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!imageResponse.ok) {
          console.warn('Image upload failed, but product was created');
          // Don't throw here - we'll consider the product creation successful even if image upload fails
        } else {
          console.log('Image uploaded successfully');
        }
      }

      // Success! Show message and prepare to redirect
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error creating product:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="container py-5">
        <h1>Add New Product</h1>

        {error && (
          <div className="alert alert-danger mt-3" role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success mt-3" role="alert">
            Product added successfully! Redirecting to dashboard...
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-4">
          {/* NAME */}
          <div className="mb-3">
            <label className="form-label">Product Name *</label>
            <input
              type="text"
              className="form-control"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* DESCRIPTION */}
          <div className="mb-3">
            <label className="form-label">Description *</label>
            <textarea
              className="form-control"
              rows="3"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* PRICE */}
          <div className="mb-3">
            <label className="form-label">Price *</label>
            <input
              type="number"
              className="form-control"
              min="0"
              step="0.01"
              required
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>

          {/* CATEGORY */}
          <div className="mb-3">
            <label className="form-label">Category</label>
            <select
              className="form-select"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">No Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* IMAGE */}
          <div className="mb-3">
            <label className="form-label">Product Image (optional)</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div className="mt-2">
                <img 
                  src={imagePreview} 
                  alt="Product preview" 
                  style={{ maxHeight: '200px', maxWidth: '100%' }} 
                  className="border rounded"
                />
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddProductPage;